import { ConfirmationDialog } from 'src/common/components/Dialog/ConfirmationDialog';
import { SimProductAction } from './constants/simProductActions';
import { isEmpty } from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import { DEFAULT_BORDER_RADIUS } from 'theme/constants.ts';
import Typography from '@mui/material/Typography';
import Box from '@mui/system/Box';
import { useExternalRowSelectionCount } from 'common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/hooks/useExternalRowSelectionCount.ts';
import { getActionTranslationName } from 'products/sim/Actions/utils/simProductActionTranslationsUtils';
import { ScrollArea } from 'common/components/ScrollArea/ScrollArea.tsx';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

type ActionConfirmDialogProps = {
  action: SimProductAction;
  notSupportedCardIccids: string[];
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
};

const CardListWrapper = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.border}`,
  borderRadius: DEFAULT_BORDER_RADIUS,
  padding: 0,
}));

const CardList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: theme.spacing(8, 12),
  margin: 0,
}));

const SummaryMessage = ({
  action,
  numberOfCards,
}: {
  numberOfCards: number;
  action: SimProductAction;
}) => {
  const { t } = useTranslation();
  const actionTranslation = getActionTranslationName(action, t);
  return (
    <Trans
      i18nKey={'products.sim.actions.confirmDialog.description'}
      values={{
        count: numberOfCards,
        action: actionTranslation,
      }}
      components={{ 1: <strong /> }}
    />
  );
};

export const ActionConfirmDialog = ({
  action,
  isPending,
  onCancel,
  onConfirm,
  notSupportedCardIccids,
}: ActionConfirmDialogProps) => {
  const { t } = useTranslation();

  const hasNotSupportedCards = !isEmpty(notSupportedCardIccids);

  const countOfSelectedCards = useExternalRowSelectionCount();

  return hasNotSupportedCards ? (
    <ConfirmationDialog
      open
      title={getActionTranslationName(action, t)}
      subTitle={t('products.sim.actions.confirmDialog.subtitle', { count: countOfSelectedCards })}
      onConfirm={onConfirm}
      onCancel={onCancel}
      confirmButtonsDisabled={isPending}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 16, mt: 16 }}>
        <Alert severity="warning" icon={<InfoOutlineIcon fontSize={'inherit'} />}>
          {t('products.sim.actions.confirmDialog.warningNotSupportedAction', {
            action: getActionTranslationName(action, t),
          })}
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Typography variant="h5" color="text.secondary">
            {t('products.sim.actions.confirmDialog.excludedSIM', {
              count: notSupportedCardIccids.length,
            })}
          </Typography>
          <CardListWrapper>
            <ScrollArea style={{ maxHeight: '30vh' }}>
              <CardList>
                {notSupportedCardIccids.map((iccid) => (
                  <Typography component={'li'} key={iccid} variant="text1">
                    {iccid}
                  </Typography>
                ))}
              </CardList>
            </ScrollArea>
          </CardListWrapper>
        </Box>
        <div data-testid={'actionConfirmationSummary'}>
          <SummaryMessage
            numberOfCards={countOfSelectedCards - notSupportedCardIccids.length}
            action={action}
          />
        </div>
      </Box>
    </ConfirmationDialog>
  ) : (
    <ConfirmationDialog
      open
      title={getActionTranslationName(action, t)}
      confirmButtonsDisabled={isPending}
      message={
        <div data-testid={'actionConfirmationSummary'}>
          <SummaryMessage
            numberOfCards={countOfSelectedCards - notSupportedCardIccids.length}
            action={action}
          />
        </div>
      }
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};
